"use client"

import { useState, useEffect, useRef } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  Users,
  User,
  FileText,
  Calendar,TextCursorInput,
  LogOut,
  MessageCircle,
  Menu,
  Mail,
  GraduationCap,
  PenTool,
  TestTube,
  Eye,
  Plus,
  Copy,
  Check,
  BookOpen,
  X,
} from "lucide-react"
import { getTeacherData, logoutTeacher, getClassData } from "./api"
import { ClassSubjectProvider, useClassSubject } from "../contexts/ClassSubjectContext"
import { useDispatch, useSelector } from "react-redux"
import {
  setClass,
  setSubject,
  setteacherId,
  setClassCode,
  setClassData,
} from "../features/teachersubject/teacherSubSlice"
import NotificationBell from "./notificationBell"
import { generatePresignedUrl } from "../postFileAws"
// Import the FCM cleanup function
import { cleanupFcmToken } from "./fcm-api"

const safeStringify = (obj) => {
  const cache = new Set()
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (cache.has(value)) {
        return "[Circular]"
      }
      cache.add(value)
    }
    return value
  })
}

const safeParse = (str) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.error("Error parsing JSON:", e)
    return null
  }
}

const storeClassData = (classResponse, dispatch) => {
  if (classResponse.statusCode === 200 && classResponse.body && classResponse.body.data) {
    const classDataString = safeStringify(classResponse.body.data)
    localStorage.setItem("classData", classDataString)
    dispatch(setClassData(classResponse.body.data))
    console.log("Class data stored in localStorage:", classDataString)
  } else {
    console.log("Invalid response from class API")
  }
}

const initializeLocalStorage = () => {
  const storedData = {
    classCode: localStorage.getItem("classCode"),
    class: localStorage.getItem("class"),
    subject: localStorage.getItem("subject"),
    section: localStorage.getItem("section"),
    classRoomId: localStorage.getItem("classRoomId"),
  }

  const isValid = Object.values(storedData).every((value) => value !== null && value !== undefined)

  if (isValid) {
    return storedData
  } else {
    const defaultData = {
      classCode: "",
      class: "",
      subject: "",
      section: "",
      classRoomId: "",
    }
    Object.entries(defaultData).forEach(([key, value]) => localStorage.setItem(key, value))
    return defaultData
  }
}

const Layout = () => {
  const dispatch = useDispatch()
  const [activeSubmenu, setActiveSubmenu] = useState(null)
  const [teacherClasses, setTeacherClasses] = useState([])
  const [showClassSubjectDropdown, setShowClassSubjectDropdown] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [teacherName, setTeacherName] = useState("")
  const [error, setError] = useState(null)
  const [currentTab, setCurrentTab] = useState("view")
  const [isCopied, setIsCopied] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [profileImageUrl, setProfileImageUrl] = useState("")
  const [isTopMenuOpen, setIsTopMenuOpen] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const dropdownRef = useRef(null)
  const profileDropdownRef = useRef(null)
  const sidebarRef = useRef(null)
  const topMenuRef = useRef(null)

  const { selectedClass, setSelectedClass, selectedSubject, setSelectedSubject, teacherId, setTeacherId } =
    useClassSubject()
  const classCode = useSelector((state) => state.teacherSub.classCode)

  const [isMobileView, setIsMobileView] = useState(false)

  // Add a ref to store the message handler unsubscribe function
  const messageHandlerUnsubscribeRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const fetchTeacherData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await getTeacherData()
        console.log("Teacher data fetched:", response)

        if (response.statusCode !== 200 || !response.body || !response.body.data) {
          throw new Error("Invalid response from API")
        }

        const data = response.body.data
        localStorage.setItem("teacherData", safeStringify(data))
        dispatch(setteacherId(data._id))
        setTeacherName(data.teacherName)
        if (data.profilePhotoS3Uri) {
          const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
          setProfileImageUrl(presignedUrl)
        }
        if (!data.createdClasses || !Array.isArray(data.createdClasses) || data.createdClasses.length === 0) {
          setTeacherClasses([])
          // Clear local storage and Redux state
          ;["class", "subject", "classCode", "section"].forEach((key) => localStorage.removeItem(key))
          dispatch(setClass(""))
          dispatch(setSubject(""))
          dispatch(setClassCode(""))
          setSelectedClass("")
          setSelectedSubject("")
        } else {
          setTeacherClasses(data.createdClasses)

          const storedData = initializeLocalStorage()

          if (!storedData.class) {
            const defaultClass = data.createdClasses[0]
            updateLocalStorageAndRedux({
              class: defaultClass.classValue,
              subject: defaultClass.subject,
              classCode: defaultClass.classCode,
            })
            localStorage.setItem("classRoomId", defaultClass.classRoomId)

            const classResponse = await getClassData(defaultClass.classRoomId)
            storeClassData(classResponse, dispatch)
          } else {
            updateLocalStorageAndRedux(storedData)
            if (!localStorage.getItem("classRoomId")) {
              const matchingClass = data.createdClasses.find(
                (c) => c.classValue === storedData.class && c.subject === storedData.subject,
              )
              if (matchingClass) {
                localStorage.setItem("classRoomId", matchingClass.classRoomId)
              }
            }
          }
        }

        setTeacherId(data._id)
        localStorage.setItem("teacherId", data._id)
      } catch (error) {
        console.error("Error fetching teacher data:", error)
        setError(error.message || "An error occurred while fetching teacher data")
        if (error.message === "No token found" || error.response?.status === 401) {
          navigate("/login")
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchTeacherData()
  }, [dispatch, navigate, setSelectedClass, setSelectedSubject, setTeacherId])

  // Replace the FCM initialization with the new service
  useEffect(() => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("teacherData"))
    if (!user || !user._id) {
      console.error("User not found in localStorage")
      return
    }

    // Initialize FCM for the application
    const initFcm = async () => {
      try {
        // Import the initialization function
        const { initializeFirebaseMessaging, setupMessageHandler } = await import("../firebase-messaging")

        // Initialize Firebase messaging
        const token = await initializeFirebaseMessaging()
        console.log("Firebase messaging initialized with token:", token)

        // Set up message handler for the Layout component
        const unsubscribe = setupMessageHandler((payload) => {
          console.log("Message received in Layout component:", payload)

          // Display notification even when app is in foreground
          if (Notification.permission === "granted") {
            const { title, body } = payload.notification || {}
            if (title) {
              const notification = new Notification(title, {
                body,
                icon: "/notification-icon.png",
              })

              // Handle notification click
              notification.onclick = () => {
                // Navigate to communication page or specific chat
                if (payload.data && payload.data.gid) {
                  navigate(`/communication?gid=${payload.data.gid}`)
                } else {
                  navigate("/communication")
                }

                // Focus window
                window.focus()

                // Close notification
                notification.close()
              }
            }
          }
        })

        // Store the unsubscribe function
        messageHandlerUnsubscribeRef.current = unsubscribe
      } catch (error) {
        console.error("Error initializing FCM in Layout:", error)
      }
    }

    // Initialize FCM
    initFcm()

    // Clean up when component unmounts
    return () => {
      // We don't actually call unsubscribe here
      // This ensures the handler persists even when the component unmounts
      console.log("Layout component unmounting, but keeping message handler active")
    }
  }, [navigate])

  const updateLocalStorageAndRedux = (data) => {
    Object.entries(data).forEach(([key, value]) => {
      localStorage.setItem(key, value)
      switch (key) {
        case "class":
          dispatch(setClass(value))
          setSelectedClass(value)
          break
        case "subject":
          dispatch(setSubject(value))
          setSelectedSubject(value)
          break
        case "classCode":
          dispatch(setClassCode(value))
          break
        case "section":
          // Assuming you have a Redux action for section
          // dispatch(setSection(value));
          break
        default:
          break
      }
    })
  }

  const toggleClassSubjectDropdown = () => {
    setShowClassSubjectDropdown((prev) => !prev)
  }

  const selectClassAndSubject = async (classData) => {
    const currentClass = localStorage.getItem("class")
    const currentSubject = localStorage.getItem("subject")

    localStorage.setItem("class", classData.classValue)
    localStorage.setItem("subject", classData.subject)
    localStorage.setItem("classCode", classData.classCode)
    localStorage.setItem("classRoomId", classData.classRoomId)

    dispatch(setClass(classData.classValue))
    dispatch(setSubject(classData.subject))
    dispatch(setClassCode(classData.classCode))

    setShowClassSubjectDropdown(false)

    try {
      const classResponse = await getClassData(classData.classRoomId)
      storeClassData(classResponse, dispatch)
      console.log("Class data after selection:", localStorage.getItem("classData"))
    } catch (error) {
      console.error("Error fetching class data:", error)
    }

    if (currentClass !== classData.classValue || currentSubject !== classData.subject) {
      navigate("/dashboard")
      window.location.reload()
    }
  }

  const toggleSidebar = () => {
    if (isMobileView) {
      setIsMobileMenuOpen(!isMobileMenuOpen)
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed)
    }
  }

  const handleLogout = async () => {
    try {
      // Get FCM token and teacherId from localStorage
      const fcmToken = localStorage.getItem("fcmToken")
      const teacherId = localStorage.getItem("teacherId")

      // If we have a token and teacherId, remove it from the database
      if (fcmToken && teacherId) {
        console.log("Removing FCM token from database during logout")

        // Use the updated cleanupFcmToken function to remove the specific token
        await cleanupFcmToken(teacherId, fcmToken)
      }

      // Clear localStorage and log out
      logoutTeacher()
      navigate("/login")
    } catch (error) {
      console.error("Error during logout:", error)
      // Still perform logout even if token removal fails
      logoutTeacher()
      navigate("/login")
    }
  }

  const getCurrentSection = () => {
    const path = location.pathname.split("/")[1]
    switch (path) {
      case "dashboard":
        return "Dashboard"
      case "students":
        return "Students"
      case "assignments":
        return "Assignments"
      case "quiz":
        return "Quiz"
      case "notes":
        return "Notes"
      case "settings":
        return "Settings"
      case "library":
        return "Library"
      case "elearning":
        return "E-Learning"
      default:
        return "Dashboard"
    }
  }
  const section = getCurrentSection().toLowerCase()
  const getCreateRoute = () => {
    switch (section) {
      case "assignments":
        return "/assignments/description"
      case "quiz":
        return "/quizzes/description"
      default:
        return `/${section}`
    }
  }

  const showActions = () => {
    
  }

  const copyClassCode = () => {
    navigator.clipboard.writeText(classCode)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 5000)
  }

  const MenuItem = ({ to, Icon, text }) => {
    const isActive = location.pathname.startsWith(to)
    const isDisabled = teacherClasses.length === 0 && to !== "/dashboard"

    const handleClick = () => {
      if (isMobileView && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }

    return (
      <li className="mb-4">
        <Link
          to={isDisabled ? "#" : to}
          className={`flex items-center py-3 px-4 rounded-lg transition-all duration-200 ${
            isActive ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
          } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleClick}
        >
          <Icon size={22} className={isSidebarCollapsed && !isMobileView ? "mx-auto" : "mr-3"} />
          {(!isSidebarCollapsed || isMobileView) && <span className="text-base font-medium">{text}</span>}
        </Link>
      </li>
    )
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false)
      }
      if (topMenuRef.current && !topMenuRef.current.contains(event.target)) {
        setIsTopMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (isMobileView && isMobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileMenuOpen, isMobileView])

  return (
    <ClassSubjectProvider value={{ selectedClass, setSelectedClass, selectedSubject, setSelectedSubject, teacherId }}>
      <div className="flex h-screen bg-gray-50 font-sans">
        {/* Left Sidebar */}
        <aside
          ref={sidebarRef}
          className={`bg-white shadow-lg transition-all duration-300 ease-in-out ${
            isMobileView ? (isMobileMenuOpen ? "w-64" : "w-0") : isSidebarCollapsed ? "w-20" : "w-64"
          } flex-shrink-0 flex flex-col fixed md:relative z-20 h-full`}
        >
          <div className="flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                {(!isSidebarCollapsed || isMobileView) && (
                  <h1 className="text-2xl font-bold text-gray-800">Teacher Portal</h1>
                )}
                {isMobileView && (
                  <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <X size={24} />
                  </button>
                )}
              </div>
              {(!isSidebarCollapsed || isMobileView) && (
                <div className="space-y-4" ref={dropdownRef}>
                  {/* Fixed height container for class/subject dropdown */}
                  <div className="h-12">
                    {isLoading ? (
                      <div className="flex items-center justify-between w-full p-3 text-sm font-medium text-left bg-gray-50 rounded-lg h-full">
                        <div className="flex items-center flex-1 min-w-0">
                          <GraduationCap size={18} className="mr-3 flex-shrink-0 text-blue-500" />
                          <div className="h-4 bg-gray-200 rounded animate-pulse flex-1 max-w-32"></div>
                        </div>
                        <ChevronDown size={16} className="text-gray-500 ml-2 flex-shrink-0" />
                      </div>
                    ) : error ? (
                      <div className="flex items-center justify-between w-full p-3 text-sm font-medium text-left bg-red-50 rounded-lg h-full">
                        <div className="flex items-center flex-1 min-w-0">
                          <GraduationCap size={18} className="mr-3 flex-shrink-0 text-red-500" />
                          <span className="truncate text-red-600">Error loading classes</span>
                        </div>
                      </div>
                    ) : (
                      <div className="relative h-full">
                        <button
                          onClick={toggleClassSubjectDropdown}
                          disabled={teacherClasses.length === 0}
                          className={`flex items-center justify-between w-full p-3 text-sm font-medium text-left bg-gray-50 rounded-lg transition-colors duration-200 h-full ${
                            teacherClasses.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
                          }`}
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <GraduationCap size={18} className="mr-3 flex-shrink-0 text-blue-500" />
                            <span className="truncate">
                              {teacherClasses.length > 0
                                ? `${selectedClass || "Select Class"} - ${selectedSubject || "Select Subject"}`
                                : "No classes available"}
                            </span>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform duration-200 ml-2 flex-shrink-0 ${
                              showClassSubjectDropdown ? "transform rotate-180" : ""
                            }`}
                          />
                        </button>
                        {showClassSubjectDropdown && teacherClasses.length > 0 && (
                          <ul className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {teacherClasses.map((classData) => (
                              <li
                                key={classData.classCode}
                                onClick={() => selectClassAndSubject(classData)}
                                className="px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer truncate"
                              >
                                {classData.classValue} - {classData.subject}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Fixed height container for class code */}
                  <div className="h-10">
                    {isLoading ? (
                      <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-between h-full">
                        <div className="flex-1 overflow-hidden">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                        </div>
                        <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ) : classCode ? (
                      <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-between h-full">
                        <div className="flex-1 overflow-hidden">
                          <span className="text-sm font-semibold text-gray-600">Class Code:</span>
                          <span className="ml-2 text-sm text-gray-800">{classCode}</span>
                        </div>
                        <button
                          onClick={copyClassCode}
                          className="p-1 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                          title="Copy class code"
                        >
                          {isCopied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                        </button>
                      </div>
                    ) : (
                      <div className="h-full"></div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <nav className="mt-1">
              <ul className="space-y-2 px-3">
                <MenuItem to="/dashboard" Icon={User} text="Dashboard" />
                <MenuItem to="/students" Icon={Users} text="Students" />
                <MenuItem to="/assignments" Icon={FileText} text="Assignments" />
                <MenuItem to="/quiz" Icon={TestTube} text="Quiz" />
                <MenuItem to="/notes" Icon={PenTool} text="Notes" />
                {/*<MenuItem to="/digitiseNotes" Icon={TextCursorInput} text="Digitised Notes" />*/}
                <MenuItem to="/library" Icon={BookOpen} text="Library" />
                <MenuItem to="/elearning" Icon={GraduationCap} text="E-Learning" />
              </ul>
            </nav>
          </div>

          {/* View / Create Section (only for Assignments and Quiz) */}
          {!isSidebarCollapsed && (!isMobileView || isMobileMenuOpen) && showActions() && (
            <div className="p-4 border-t border-gray-200">
              <h2 className="text-sm font-semibold mb-3 text-gray-600">Actions</h2>
              <div className="flex space-x-2">
                <Link
                  to={`/${getCurrentSection().toLowerCase()}`}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors duration-200"
                >
                  <Eye size={16} className="mr-2" />
                  <span className="text-sm font-medium">View</span>
                </Link>
                <Link
                  to={getCreateRoute()}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 transition-colors duration-200"
                >
                  <Plus size={16} className="mr-2" />
                  <span className="text-sm font-medium">Create</span>
                </Link>
              </div>
            </div>
          )}

          <div className="p-4 border-t border-gray-200">
            {!isSidebarCollapsed && (!isMobileView || isMobileMenuOpen) && (
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-full p-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors duration-200"
              >
                <LogOut size={18} className="mr-2" />
                <span>Logout</span>
              </button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Top Navigation Bar */}
          <header className="bg-white shadow-sm py-2 px-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Menu size={24} />
                </button>
                <h1 className="text-xl font-bold text-gray-800 ml-4 mt-2 truncate">
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </h1>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-4">
                <Link to="/calendar" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                  <Calendar size={20} />
                </Link>
                <NotificationBell />
                <Link to="/announcements" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                  <Mail size={20} />
                </Link>
                <Link to="/communication" className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                  <MessageCircle size={20} />
                </Link>
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center focus:outline-none"
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl || "/placeholder.svg"}
                        alt="User"
                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User size={18} className="text-gray-500" />
                      </div>
                    )}

                    <span className="text-sm font-medium ml-2">{teacherName}</span>
                    <ChevronDown size={16} className="ml-1 text-gray-500" />
                  </button>

                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <Link
                        to="/Profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsProfileDropdownOpen(false)
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="md:hidden flex items-center">
                <div className="relative" ref={topMenuRef}>
                  <button
                    onClick={() => setIsTopMenuOpen(!isTopMenuOpen)}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl || "/placeholder.svg"}
                        alt="User"
                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User size={18} className="text-gray-500" />
                      </div>
                    )}
                  </button>

                  {isTopMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-800">{teacherName}</p>
                      </div>
                      <div className="py-2 grid grid-cols-4 gap-1 px-2 border-b border-gray-100">
                        <Link to="/calendar" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                          <Calendar size={20} className="text-gray-600" />
                          <span className="text-xs mt-1">Calendar</span>
                        </Link>
                        <div className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                          <NotificationBell iconOnly={true} />
                          <span className="text-xs -mt-2">Alerts</span>
                        </div>
                        <Link to="/announcements" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                          <Mail size={20} className="text-gray-600" />
                          <span className="text-xs mt-1">Mail</span>
                        </Link>
                        <Link to="/communication" className="flex flex-col items-center p-2 rounded hover:bg-gray-100">
                          <MessageCircle size={20} className="text-gray-600" />
                          <span className="text-xs mt-1">Chat</span>
                        </Link>
                      </div>
                      <Link
                        to="/Profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsTopMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsTopMenuOpen(false)
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Dynamic Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <Outlet />
          </div>
        </main>
      </div>
    </ClassSubjectProvider>
  )
}

export default Layout
